import { useState, useEffect } from "react";
import { 
  Mail, 
  Send, 
  CheckCircle,
  Clock,
  AlertCircle,
  XCircle,
  RefreshCw,
  MessageSquare
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { getEmailLogs, sendEmail, markEmailReplied, getCampaigns } from "../lib/api";
import { motion } from "framer-motion";

const StatusIcon = ({ status }) => {
  const config = {
    queued: { icon: Clock, class: "text-muted-foreground" },
    sent: { icon: Send, class: "text-blue-500" },
    opened: { icon: Mail, class: "text-yellow-500" },
    replied: { icon: CheckCircle, class: "text-green-500" },
    bounced: { icon: XCircle, class: "text-red-500" },
    failed: { icon: AlertCircle, class: "text-red-500" }
  };
  
  const { icon: Icon, class: className } = config[status] || config.queued;
  
  return <Icon className={`w-4 h-4 ${className}`} />;
};

const StatusBadge = ({ status }) => {
  const config = {
    queued: { class: "bg-accent text-muted-foreground", label: "Queued" },
    sent: { class: "bg-blue-500/15 text-blue-500", label: "Sent" },
    opened: { class: "badge-warning", label: "Opened" },
    replied: { class: "badge-success", label: "Replied" },
    bounced: { class: "badge-error", label: "Bounced" },
    failed: { class: "badge-error", label: "Failed" }
  };
  
  const { class: className, label } = config[status] || config.queued;
  
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${className}`}>
      <StatusIcon status={status} />
      {label}
    </span>
  );
};

export default function EmailQueue() {
  const [emails, setEmails] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCampaign, setSelectedCampaign] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [sendingId, setSendingId] = useState(null);

  useEffect(() => {
    fetchData();
  }, [selectedCampaign, selectedStatus]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [emailsRes, campaignsRes] = await Promise.all([
        getEmailLogs({
          campaign_id: selectedCampaign !== "all" ? selectedCampaign : undefined,
          status: selectedStatus !== "all" ? selectedStatus : undefined
        }),
        getCampaigns()
      ]);
      setEmails(emailsRes.data);
      setCampaigns(campaignsRes.data);
    } catch (error) {
      toast.error("Failed to fetch email data");
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async (email) => {
    setSendingId(email.id);
    try {
      await sendEmail(email.campaign_id, email.website_id);
      toast.success("Email sent successfully");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send email");
    } finally {
      setSendingId(null);
    }
  };

  const handleMarkReplied = async (emailId) => {
    try {
      await markEmailReplied(emailId);
      toast.success("Marked as replied");
      fetchData();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const getCampaignName = (campaignId) => {
    const campaign = campaigns.find(c => c.id === campaignId);
    return campaign?.name || "Unknown Campaign";
  };

  const stats = {
    total: emails.length,
    queued: emails.filter(e => e.status === "queued").length,
    sent: emails.filter(e => e.status === "sent").length,
    replied: emails.filter(e => e.status === "replied").length
  };

  return (
    <div className="space-y-6" data-testid="emails-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Email Queue</h1>
        <p className="text-muted-foreground mt-1">
          Track and manage your outreach emails
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-card border-border">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-md bg-accent flex items-center justify-center">
              <Mail className="w-5 h-5 text-muted-foreground" />
            </div>
            <div>
              <p className="text-2xl font-bold">{stats.total}</p>
              <p className="text-xs text-muted-foreground">Total Emails</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-md bg-yellow-500/10 flex items-center justify-center">
              <Clock className="w-5 h-5 text-yellow-500" />
            </div>
            <div>
              <p className="text-2xl font-bold">{stats.queued}</p>
              <p className="text-xs text-muted-foreground">Queued</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-md bg-blue-500/10 flex items-center justify-center">
              <Send className="w-5 h-5 text-blue-500" />
            </div>
            <div>
              <p className="text-2xl font-bold">{stats.sent}</p>
              <p className="text-xs text-muted-foreground">Sent</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-md bg-green-500/10 flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-green-500" />
            </div>
            <div>
              <p className="text-2xl font-bold">{stats.replied}</p>
              <p className="text-xs text-muted-foreground">Replied</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="bg-card border-border">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <Select value={selectedCampaign} onValueChange={setSelectedCampaign}>
                <SelectTrigger data-testid="campaign-filter">
                  <SelectValue placeholder="Filter by campaign" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Campaigns</SelectItem>
                  {campaigns.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1">
              <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                <SelectTrigger data-testid="status-filter">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="queued">Queued</SelectItem>
                  <SelectItem value="sent">Sent</SelectItem>
                  <SelectItem value="replied">Replied</SelectItem>
                  <SelectItem value="bounced">Bounced</SelectItem>
                  <SelectItem value="failed">Failed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button variant="outline" onClick={fetchData} data-testid="refresh-btn">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Email List */}
      <Card className="bg-card border-border overflow-hidden">
        {loading ? (
          <div className="p-8" data-testid="loading-skeleton">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="skeleton-row">
                <div className="skeleton-cell flex-1" />
                <div className="skeleton-cell w-24" />
                <div className="skeleton-cell w-20" />
              </div>
            ))}
          </div>
        ) : emails.length === 0 ? (
          <CardContent className="empty-state" data-testid="empty-state">
            <Mail className="w-16 h-16" strokeWidth={1} />
            <h3 className="text-lg font-semibold mt-4">No emails in queue</h3>
            <p className="text-sm max-w-sm mt-2">
              Launch a campaign to start sending outreach emails.
            </p>
          </CardContent>
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table" data-testid="emails-table">
              <thead>
                <tr>
                  <th>Recipient</th>
                  <th>Subject</th>
                  <th>Campaign</th>
                  <th>Status</th>
                  <th>Reminder</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {emails.map((email) => (
                  <motion.tr 
                    key={email.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    data-testid={`email-row-${email.id}`}
                  >
                    <td>
                      <div className="flex items-center gap-2">
                        <Mail className="w-4 h-4 text-muted-foreground" />
                        <span className="font-mono text-sm">{email.recipient_email}</span>
                      </div>
                    </td>
                    <td>
                      <span className="truncate max-w-[200px] block" title={email.subject}>
                        {email.subject}
                      </span>
                    </td>
                    <td>
                      <span className="text-sm text-muted-foreground">
                        {getCampaignName(email.campaign_id)}
                      </span>
                    </td>
                    <td>
                      <StatusBadge status={email.status} />
                    </td>
                    <td>
                      <span className="text-sm text-muted-foreground">
                        #{email.reminder_number || 0}
                      </span>
                    </td>
                    <td>
                      <div className="flex items-center gap-1">
                        {email.status === "queued" && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleSend(email)}
                            disabled={sendingId === email.id}
                            data-testid={`send-${email.id}`}
                          >
                            {sendingId === email.id ? (
                              <RefreshCw className="w-4 h-4 animate-spin" />
                            ) : (
                              <>
                                <Send className="w-4 h-4 mr-1" />
                                Send
                              </>
                            )}
                          </Button>
                        )}
                        {email.status === "sent" && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleMarkReplied(email.id)}
                            data-testid={`mark-replied-${email.id}`}
                          >
                            <MessageSquare className="w-4 h-4 mr-1" />
                            Mark Replied
                          </Button>
                        )}
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
